import { BlurEvent, ChangeEvent, DataSourceMode, DataSourceModeChangeEvent, DropdownItem, DropdownSelectionEvent,
    LookupModelSearchEvent, LookupModelSelectionEvent, Tab, Textbox, ValidationResult
} from "@mcleod/components";
import { Api, Model, ModelRow, ModelRowType, ObjectUtil, StringUtil } from "@mcleod/core";
import { CommonDialogs } from "@mcleod/common";
import { ModelContact, ModelZones } from "@mcleod/general";
import { AutogenLayoutRecStopEntry } from "./autogen/AutogenLayoutRecStopEntry";
import { ModelLocation } from "./models/ModelLocation";
import { OrderStop } from "./OrderStop";

export class RecStopEntry extends AutogenLayoutRecStopEntry {static readonly STOP_TYPE = ["SD"];

    public getTimezone: boolean = false;
    public stopType: string = null;
    private _orderId: string = null;

    public locationChanged: () => void;
    private locationChangeListener = event => this.handleLocationChange(event);

    override onLoad() {
        this.mainDataSource.preventFocusFirstField = true;
        this.locationLocation.addLookupModelSelectionListener(this.locationChangeListener);
        this.locationLocation.addChangeListener((event: ChangeEvent) => {
            const rowLocation: ModelRow<any> = event.newValue;
            const enableCityState = StringUtil.isEmptyString(rowLocation.data?.city_id);
            this.citystateCityStateZip.enabled = enableCityState;
            if (enableCityState) {
                this.activeRow.set("zone_id", null);
                this.textboxZoneId.displayData(this.activeRow, null, 0);
                this.textboxContactName.clear();
                this.textboxContactPhone.clear();
                this.textboxContactEmail.clear();
            }
        })
    }

    private async handleLocationChange(event: LookupModelSelectionEvent) {
        const locationId = event.selection?.get("id") as string;
        this.mainDataSource.redisplayRow(0, this.activeRow ?? null, null);
        this.citystateCityStateZip["_lookupModelDataForUpdate"] = null;
        if (locationId) {
            this.textboxContactName.clear();
            this.textboxContactPhone.clear();
            this.textboxContactEmail.clear();
            const rowLocation: ModelRow<any> = await new ModelLocation().searchSingle({ id: locationId, "updating_stop": true, get_timezone: this.getTimezone }, "lme/dispatch/Location");
            if (rowLocation == null) {
                CommonDialogs.showDialog("Location [" + locationId + "] not found in database.");
                this.citystateCityStateZip.enabled = true;
            } else {
                this.textboxLocationId.quickInfoOverrideKeyValue = locationId;
                this.setStopContactInfo(locationId);
                this.citystateCityStateZip.enabled = false;
                //There is logic in Location fields to set location-based model row values
                //This logic does not appear to set Address2 bc it is not part of the textCombined data
                this.mainDataSource.activeRow.set("address2", rowLocation.get("address2", null))
                const zoneResponse = await new ModelZones().searchSingle({ city_id: rowLocation.data?.city_id });
                if (zoneResponse != null) {
                    const lmModelRow = new ModelRow(this.textboxZoneId.lookupModel, false, {
                        id: zoneResponse.get("id"),
                        descr: zoneResponse.get("descr")
                    });
                    lmModelRow.type = ModelRowType.LOOKUP_MODEL_DATA;
                    this.activeRow.setLookupModelData("zone_id", lmModelRow);
                    this.activeRow.set("zone_id", zoneResponse.get("id"));
                    this.textboxZoneId.displayData(this.activeRow, null, 0);
                }
                if (event.hasSelection()) {
                    this.locationChanged?.();
                }
            }
        } else {
            this.citystateCityStateZip.enabled = true;
            if (event.hasDeselection())
                this.citystateCityStateZip.clearAllFields();
        }
        this.citystateCityStateZip.displayData(this.activeRow ?? null, null, 0);
    }

    async setStopContactInfo(locationId: string) {
        await new ModelContact().searchSingle({ "parent_row_type": "L", "parent_row_id": locationId }).then(rowContact => {
            if (rowContact == null) {
                return;
            }
            this.mainDataSource.activeRow.setLookupModelData("contact_name", rowContact.data);
            this.mainDataSource.activeRow.set("contact_name", rowContact.data["name"]);
            this.mainDataSource.activeRow.set("phone", rowContact.data["phone"]);
            this.mainDataSource.activeRow.set("contact_email", rowContact.data["email"]);
        });
    }

    textboxContactNameBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        if (this.mainDataSource.activeRow.get("location_id")) {
            event.filter.parent_row_type = "L";
            event.filter.parent_row_id = this.mainDataSource.activeRow.get("location_id");
            event.filter.is_active = "Y";
        } else {
            event.preventDefault();
        }
    }

    textboxContactNameOnLookupModelSelection(event: LookupModelSelectionEvent) {
        if (event.hasSelection()) {
            const selectedRow = event.selection;
            this.mainDataSource.activeRow.setLookupModelData("contact_name", selectedRow);
            this.mainDataSource.activeRow.set("phone", selectedRow.get("phone"));
            this.mainDataSource.activeRow.set("contact_email", selectedRow.get("email"));
            this.mainDataSource.displayDataInBoundComponents();
        }
    }

    /** This is an event handler for the onBlur event of citystateCityStateZip. */
    citystateCityStateZipOnBlur(event: BlurEvent) {
        if (!this.activeRow.isNull("city_id")) {
            if (ObjectUtil.containsKey(this.activeRow.getChangedData(), "city_id") && this.citystateCityStateZip.enabled) {
                this.locationChanged?.();
            }
        }
    }

    public getStop() {
        return this.mainDataSource.activeRow;
    }

    public newEntry(props?) {
        this.mainDataSource.clear();
        return this.sourceStopRecOrder.createBlankRow().then(row => {

            this.citystateCityStateZip.enabled = true;

            row.set("order_id", this.orderId);
            if (props) {
                row.set({...props});
            }

            const notesRefsLayout = this.layoutStopNotesAndRefNbrs;
            const stopNoteTable = notesRefsLayout.tableStopComments;
            const refNbrTable = notesRefsLayout.tableStopReferenceNumbers;

            row.set("stop_notes", stopNoteTable.data);
            row.set("reference_numbers", refNbrTable.data);
            row.resetOriginalData();
            this.mainDataSource.setRowsAndMode(DataSourceMode.ADD, [row]);
            [stopNoteTable, refNbrTable].forEach(table => {
                OrderStop.setTableParentDataSource(table, this.mainDataSource);
                table.dataSource.mode = DataSourceMode.UPDATE;
                if (table.quickAddData) {
                    table.resetQuickAddRow();
                }
                notesRefsLayout.updateStopsTabCaption(table);
                const parentTab = table.parent as Tab;
                if (parentTab.expanded) {
                    parentTab.expanded = false;
                }
            });

            notesRefsLayout.stopRow = row;
            this.mainDataSource.displayDataInBoundComponents();
        })
    }

    public get orderId(): string {
        return this._orderId;
    }
    public set orderId(value: string) {
        this._orderId = value;
        this.sourceRecurringOrder.search({id:value});
    }

    sourceStopAfterModeChange(event: DataSourceModeChangeEvent) {
        if (event.newMode === DataSourceMode.ADD) {
            const items = this.textboxStopType.resolveItems();
            this.textboxStopType.items = items.filter((item: DropdownItem) => {
               return item.value !== "SD";
            });
        }
    }
}
